import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Card, Button, Spinner } from "../components/ui";
import api from "../lib/api";
import { Users, Check, ShieldAlert } from "lucide-react";

export default function AcceptInvite() {
  const { t, user, authLoading, loadWorkspaces, switchWorkspace } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (token) localStorage.setItem("pending_invite_token", token);
    api.get(`/team/invite/preview?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setPreview(data))
      .catch((e) => setError(e?.response?.data?.detail || t("invite_invalid")));
  }, [token, t]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/team/invite/accept?token=${encodeURIComponent(token)}`);
      localStorage.removeItem("pending_invite_token");
      await loadWorkspaces();
      await switchWorkspace(data.workspace_id);
      navigate("/");
    } catch (e) {
      setError(e?.response?.data?.detail || t("invite_invalid"));
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md" data-testid="accept-invite-card">
        <Link to="/" className="flex items-center gap-2 mb-5 group w-fit" data-testid="invite-logo-home">
          <div className="w-8 h-8 rounded-lg bg-text-primary text-background flex items-center justify-center font-heading font-black text-sm transition-transform group-hover:scale-105">CO</div>
          <span className="font-heading font-bold">{t("app_name")}</span>
        </Link>

        {error ? (
          <div className="text-center py-6" data-testid="invite-error">
            <ShieldAlert className="w-8 h-8 mx-auto text-red-400 mb-3" />
            <p className="text-sm text-text-secondary">{error}</p>
            <Button className="mt-5" onClick={() => navigate("/")}>{t("invite_go_home")}</Button>
          </div>
        ) : !preview ? (
          <div className="flex justify-center py-10"><Spinner className="w-6 h-6 text-text-secondary" /></div>
        ) : (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-xl bg-surface-hover flex items-center justify-center">
                <Users className="w-5 h-5 text-text-secondary" />
              </span>
              <div>
                <div className="text-sm text-text-secondary">{t("invite_you_invited")}</div>
                <div className="font-heading font-semibold text-lg">{preview.workspace_name}</div>
              </div>
            </div>
            <div className="rounded-xl border p-3 text-sm space-y-1.5 mb-5">
              <div className="flex justify-between"><span className="text-text-secondary">{t("invite_role")}</span><span className="font-medium capitalize">{t(`role_${preview.role}`)}</span></div>
              <div className="flex justify-between"><span className="text-text-secondary">{t("invite_email")}</span><span className="font-medium">{preview.email}</span></div>
              <div className="flex justify-between gap-3">
                <span className="text-text-secondary shrink-0">{t("invite_access")}</span>
                <span className="font-medium text-right">
                  {preview.scope === "group" ? t("invite_whole_group")
                    : (preview.entity_names || []).join(", ") || t("invite_specific_entities")}
                </span>
              </div>
            </div>

            {authLoading ? (
              <div className="flex justify-center py-2"><Spinner className="w-5 h-5 text-text-secondary" /></div>
            ) : user ? (
              user.email?.toLowerCase() === preview.email?.toLowerCase() ? (
                <Button className="w-full" onClick={accept} disabled={accepting} data-testid="accept-invite-button">
                  <Check className="w-4 h-4 mr-2" /> {accepting ? t("loading") : t("invite_accept")}
                </Button>
              ) : (
                <p className="text-xs text-red-400 text-center" data-testid="invite-wrong-account">
                  {t("invite_wrong_account").replace("{email}", preview.email)}
                </p>
              )
            ) : (
              <Button className="w-full" onClick={() => navigate("/login")} data-testid="invite-login-button">
                {t("invite_login_to_accept")}
              </Button>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
